const STORAGE_KEY = 'mfa_device_trust';
const TRUST_MS = 5 * 24 * 60 * 60 * 1000;

export type MfaDeviceTrust = {
  userId: string;
  expiresAt: number;
  trustVersion: number;
};

function readAll(): MfaDeviceTrust[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MfaDeviceTrust[]) : [];
  } catch {
    return [];
  }
}

function writeAll(entries: MfaDeviceTrust[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

export function getMfaTrustVersion(user: {
  app_metadata?: Record<string, unknown> | null;
} | null | undefined): number {
  const raw = user?.app_metadata?.mfa_trust_version;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function isMfaRequired(user: {
  app_metadata?: Record<string, unknown> | null;
} | null | undefined): boolean {
  return Boolean(user?.app_metadata?.mfa_required);
}

/** Valid 5-day remember-device trust for this user + trust version. */
export function hasValidMfaDeviceTrust(
  userId: string,
  trustVersion: number
): boolean {
  const now = Date.now();
  const entries = readAll().filter((e) => e.expiresAt > now);
  if (entries.length !== readAll().length) writeAll(entries);
  return entries.some(
    (e) =>
      e.userId === userId &&
      e.trustVersion === trustVersion &&
      e.expiresAt > now
  );
}

export function saveMfaDeviceTrust(userId: string, trustVersion: number): void {
  const now = Date.now();
  const next = readAll()
    .filter((e) => e.expiresAt > now && e.userId !== userId)
    .concat([
      {
        userId,
        trustVersion,
        expiresAt: now + TRUST_MS,
      },
    ]);
  writeAll(next);
}

export function clearMfaDeviceTrustForUser(userId: string): void {
  writeAll(readAll().filter((e) => e.userId !== userId));
}
